import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useUtilityStore } from "../zustand/utilityStores";
import "./ChatLobby.css";

const API_BASE = "https://app.sexy.ai/api";
const LOBBY_CHANNEL_NAME = "pornpen-lobby";

interface ChatMessage {
  _id: string;
  senderId: string;
  senderUsername: string;
  content: string;
  createdAt: string;
}

interface LobbyChannel {
  _id: string;
  name: string;
  memberCount?: number;
}

export function ChatLobby() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [lobbyChannel, setLobbyChannel] = useState<LobbyChannel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const setSignInRequired = useUtilityStore((state) => state.setSignInRequired);
  const isPro = useUtilityStore((state) => state.isPro);

  // Check auth state and reset user data on account change
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      // Reset user data when auth changes to force refetch
      setCurrentUserId(null);
      setCurrentUsername(null);
    });
    return () => unsubscribe();
  }, []);

  // Fetch user session data
  const fetchUserSession = useCallback(async (): Promise<{ jwt: string; user?: { _id: string; username?: string } } | null> => {
    const sexySessionID = localStorage.getItem("sexySessionID");
    if (!sexySessionID) return null;

    try {
      const res = await fetch("https://autoapi.cdn2.io/APIProxy/GetUserSession", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sexySessionID }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.jwt) {
          localStorage.setItem("api2JWT", data.jwt);
          if (data.user?._id) {
            setCurrentUserId(data.user._id);
          }
          if (data.user?.username) {
            setCurrentUsername(data.user.username);
          }
          return data;
        }
      }
    } catch (err) {
      console.error("Failed to get user session:", err);
    }
    return null;
  }, []);

  // Get JWT for API calls
  const getJWT = useCallback(async (): Promise<string | null> => {
    let jwt = localStorage.getItem("api2JWT");
    if (jwt) {
      // If we have JWT but no username, fetch user data
      if (!currentUsername) {
        await fetchUserSession();
      }
      return jwt;
    }

    const session = await fetchUserSession();
    return session?.jwt || null;
  }, [currentUsername, fetchUserSession]);

  // Fetch or create pornpen-lobby channel
  const fetchLobby = useCallback(async () => {
    const jwt = await getJWT();
    if (!jwt) {
      setError("Please sign in to access the chat");
      return;
    }

    try {
      // First, try to find existing pornpen-lobby channel in public channels
      const searchRes = await fetch(`${API_BASE}/Messaging/channels?limit=100`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (searchRes.ok) {
        const searchData = await searchRes.json();
        if (searchData.success && searchData.channels) {
          const existingChannel = searchData.channels.find(
            (ch: LobbyChannel) => ch.name === LOBBY_CHANNEL_NAME
          );
          if (existingChannel) {
            setLobbyChannel(existingChannel);
            return existingChannel._id;
          }
        }
      }

      // If not found, create the pornpen-lobby channel as a room type
      const createRes = await fetch(`${API_BASE}/Messaging/channels`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: LOBBY_CHANNEL_NAME,
          type: "room",
          description: "Pornpen community chat lobby",
          settings: { isPublic: true },
        }),
      });

      if (createRes.ok) {
        const createData = await createRes.json();
        if (createData.success && createData.channel) {
          setLobbyChannel(createData.channel);
          return createData.channel._id;
        }
      }

      setError("Failed to load chat lobby");
    } catch (err) {
      console.error("Failed to fetch lobby:", err);
      setError("Failed to connect to chat");
    }
    return null;
  }, [getJWT]);

  // Fetch messages
  const fetchMessages = useCallback(async (channelId: string) => {
    const jwt = await getJWT();
    if (!jwt || !channelId) return;

    try {
      const res = await fetch(`${API_BASE}/Messaging/channels/${channelId}?limit=50`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.messages) {
          setMessages(data.messages);
          setError(null);
        }
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  }, [getJWT]);

  // Scroll to bottom helper
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Initialize chat when opened
  useEffect(() => {
    if (isOpen && isLoggedIn) {
      setIsLoading(true);
      fetchLobby().then((channelId) => {
        if (channelId) {
          fetchMessages(channelId).finally(() => {
            setIsLoading(false);
            // Scroll to bottom on initial load
            setTimeout(scrollToBottom, 100);
          });
        } else {
          setIsLoading(false);
        }
      });
    }
  }, [isOpen, isLoggedIn, fetchLobby, fetchMessages, scrollToBottom]);

  // Poll for new messages
  useEffect(() => {
    if (isOpen && isLoggedIn && lobbyChannel) {
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(lobbyChannel._id);
      }, 5000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [isOpen, isLoggedIn, lobbyChannel, fetchMessages]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !lobbyChannel || isSending) return;

    const jwt = await getJWT();
    if (!jwt) {
      setError("Please sign in to send messages");
      return;
    }

    setIsSending(true);
    try {
      const res = await fetch(`${API_BASE}/Messaging/messages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          channelId: lobbyChannel._id,
          content: newMessage.trim(),
          contentType: "text",
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.message) {
          setMessages((prev) => [...prev, data.message]);
          setNewMessage("");
          setError(null);
          // Scroll to bottom after sending
          setTimeout(scrollToBottom, 100);
        }
      } else {
        setError("Failed to send message");
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      setError("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  const handleLoginClick = () => {
    setSignInRequired(true);
    setIsOpen(false);
  };

  // Only show chat lobby for pro users
  if (!isPro) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        className={`chat-lobby-toggle ${isOpen ? "chat-lobby-toggle-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Chat Lobby"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-lobby-window">
          {/* Header */}
          <div className="chat-lobby-header">
            <div className="chat-lobby-header-info">
              <h3>Chat Lobby</h3>
            </div>
            <button className="chat-lobby-close" onClick={() => setIsOpen(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          {/* Error Banner */}
          {error && <div className="chat-lobby-error">{error}</div>}

          {/* Content */}
          {!isLoggedIn ? (
            <div className="chat-lobby-login-prompt">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#6366f1">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
              </svg>
              <p>Sign in to join the chat and connect with other users!</p>
              <button className="chat-lobby-login-btn" onClick={handleLoginClick}>
                Sign In
              </button>
            </div>
          ) : isLoading ? (
            <div className="chat-lobby-loading">Loading chat...</div>
          ) : (
            <>
              {/* Messages */}
              <div className="chat-lobby-messages">
                {messages.length === 0 ? (
                  <div className="chat-lobby-empty">
                    No messages yet. Be the first to say hi!
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwnMessage = msg.senderId === currentUserId;

                    return (
                      <div
                        key={msg._id}
                        className={`chat-message ${isOwnMessage ? "own-message" : ""}`}
                      >
                        <div className="chat-message-header">
                          <span className="chat-message-username">
                            {msg.senderUsername || "Anonymous"}
                          </span>
                          <span className="chat-message-time">
                            {formatTime(msg.createdAt)}
                          </span>
                        </div>
                        <div className="chat-message-content">{msg.content}</div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {currentUsername && (
                <div className="chat-lobby-username">
                  Chatting as <strong>{currentUsername}</strong>
                </div>
              )}
              <div className="chat-lobby-input-container">
                <input
                  type="text"
                  className="chat-lobby-input"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxLength={500}
                />
                <button
                  className="chat-lobby-send"
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || isSending}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
